import { useMemo, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useConfirmDialog,
  useToast,
} from '@/components/ui';
import { SearchIcon } from '@/components/icons';
import {
  useFeatureFlags,
  useFeatureFlagActions,
  type FeatureFlagDto,
  type FeatureFlagSource,
} from '@/lib/feature-flags-store';

import { formatarData } from '@/lib/format-date';
/**
 * /admin/feature-flags — "Recursos em teste": liga/desliga funcionalidades em
 * runtime, sem redeploy (FEAT-V2-019 UI).
 *
 * Layout:
 *   - PageHeader + busca por nome (humano ou identificador) + filtro de área.
 *   - Tabela: Recurso | Ligado | Origem | Última alteração | (ações).
 *   - Toggle inline via <Switch> dispara PUT /admin/feature-flags/:name.
 *   - Toggle em recurso crítico (área='Security' + critica=true) pede
 *     ConfirmDialog destructive ANTES de propagar.
 *
 * FASE-C (copy): o nome humano vem primeiro; o identificador técnico
 * (MAIÚSCULA_COM_UNDERLINE) fica como detalhe secundário em <code>. Rótulos de
 * origem deixaram de ser DB/ENV/DEFAULT.
 *
 * Cache: backend tem TTL 5s no isFlagEnabled, então admin que acabou de
 * toggle pode esperar até 5s pra middleware refletir em outros endpoints.
 * Esta UI atualiza imediatamente (invalidate da TanStack Query).
 */

interface FlagMetadata {
  /** Nome humano pt-BR exibido na coluna "Recurso". */
  label: string;
  /** Descrição curta exibida na linha (max ~120 chars). */
  descricao: string;
  /** Área pra filtro do combobox. */
  categoria: FlagCategoria;
  /** Quando true, toggle pede confirmação destructive. */
  critica?: boolean;
}

/**
 * Os VALORES são identificadores estáveis (usados no filtro e nos testes);
 * o que aparece na tela é o rótulo em `CATEGORIA_LABEL`.
 */
type FlagCategoria =
  | 'Security'
  | 'Cards'
  | 'Notifications'
  | 'Calendar'
  | 'OS Digital'
  | 'Automations'
  | 'UX'
  | 'Outro';

const CATEGORIAS: FlagCategoria[] = [
  'Security',
  'Cards',
  'Notifications',
  'Calendar',
  'OS Digital',
  'Automations',
  'UX',
  'Outro',
];

const CATEGORIA_LABEL: Record<FlagCategoria, string> = {
  Security: 'Segurança',
  Cards: 'Cards',
  Notifications: 'Notificações',
  Calendar: 'Agenda e tarefas',
  'OS Digital': 'Ordens de serviço',
  Automations: 'Automações',
  UX: 'Interface',
  Outro: 'Outros',
};

const FLAG_METADATA: Record<string, FlagMetadata> = {
  // ─── Segurança (CRÍTICAS) ───────────────────────────────────────────
  DEV_LOGIN_ENABLED: {
    label: 'Entrar sem senha (uso interno)',
    descricao:
      'Permite entrar no sistema sem usuário e senha. Serve só pra testes internos — ligado no ar, qualquer pessoa vira administrador.',
    categoria: 'Security',
    critica: true,
  },
  MCP_CONECTOR_ENABLED: {
    label: 'Conexão dos sócios com o Claude',
    descricao:
      'Deixa os sócios autorizados consultarem o sistema pelo Claude, só para leitura. Desligar corta o acesso na hora.',
    categoria: 'Security',
    critica: true,
  },
  WORKFLOW_COLUMN_DELETE_ENABLED: {
    label: 'Excluir colunas do quadro',
    descricao:
      'Permite ao administrador apagar colunas criadas por ele nos quadros de trabalho (colunas com cards dentro seguem bloqueadas).',
    categoria: 'Security',
  },

  // ─── Cards ────────────────────────────────────────────────────────
  CARD_COMMENTS_ENABLED: {
    label: 'Comentários no card',
    descricao: 'Mostra a aba de comentários dentro de cada card.',
    categoria: 'Cards',
  },
  CARD_COLLABORATORS_ENABLED: {
    label: 'Colaboradores no card',
    descricao: 'Permite colocar mais de uma pessoa trabalhando no mesmo card.',
    categoria: 'Cards',
  },
  ACTIVITY_TIMELINE_ENABLED: {
    label: 'Histórico do card',
    descricao:
      'Mostra tudo que aconteceu no card em ordem: mudou de coluna, trocou de responsável, e por aí vai.',
    categoria: 'Cards',
  },
  CARD_MUTE_ENABLED: {
    label: 'Silenciar um card',
    descricao: 'Botão pra parar de receber avisos de um card específico.',
    categoria: 'Cards',
  },
  ACTIVITY_FILTER_ENABLED: {
    label: 'Filtros no histórico',
    descricao: 'Permite filtrar o que aparece na aba de histórico do card.',
    categoria: 'Cards',
  },
  AUTO_WATCH_ENABLED: {
    label: 'Acompanhar ao comentar',
    descricao: 'Quem comenta num card passa a receber os avisos dele automaticamente.',
    categoria: 'Cards',
  },
  COMMENT_ATTACHMENTS_ENABLED: {
    label: 'Anexos nos comentários',
    descricao: 'Permite anexar arquivos junto com o comentário.',
    categoria: 'Cards',
  },
  COMMENT_REACTIONS_ENABLED: {
    label: 'Reações nos comentários',
    descricao: 'Permite reagir a um comentário com uma carinha, em vez de responder.',
    categoria: 'Cards',
  },
  COMMENT_MARKDOWN_ENABLED: {
    label: 'Texto formatado nos comentários',
    descricao: 'Aceita negrito, itálico, listas e links dentro do comentário.',
    categoria: 'Cards',
  },
  COMMENT_PHOTO_CAPTURE_ENABLED: {
    label: 'Foto no comentário',
    descricao: 'Botão pra tirar foto na hora pelo celular e mandar no comentário.',
    categoria: 'Cards',
  },
  COMMENT_AUDIO_NOTE_ENABLED: {
    label: 'Áudio no comentário',
    descricao: 'Botão pra gravar um recado de voz pelo celular.',
    categoria: 'Cards',
  },
  COMMENT_SEARCH_ENABLED: {
    label: 'Busca nos comentários',
    descricao: 'Campo pra procurar uma palavra dentro dos comentários do card.',
    categoria: 'Cards',
  },
  COMMENT_PIN_ENABLED: {
    label: 'Fixar comentário',
    descricao: 'O administrador pode deixar um comentário fixo no topo do card.',
    categoria: 'Cards',
  },
  NOTAS_INTERNAS_ENABLED: {
    label: 'Notas internas',
    descricao:
      'Aba de recados que só a equipe do fluxo de trabalho enxerga — o cliente nunca vê.',
    categoria: 'Cards',
  },

  // ─── Notificações ────────────────────────────────────────────────
  NOTIFICATION_INAPP_ENABLED: {
    label: 'Avisos dentro do sistema',
    descricao: 'Sininho no topo da tela mostrando o que aconteceu.',
    categoria: 'Notifications',
  },
  NOTIFICATION_PREFERENCES_ENABLED: {
    label: 'Cada um escolhe seus avisos',
    descricao:
      'Libera a tela onde a pessoa decide o que quer receber e por onde (sistema, e-mail, WhatsApp).',
    categoria: 'Notifications',
  },

  // ─── Agenda e tarefas ────────────────────────────────────────────
  TASKS_ENABLED: {
    label: 'Tarefas com data e hora',
    descricao: 'Liga as tarefas: sem horário, só com início, ou com início e fim.',
    categoria: 'Calendar',
  },
  TASKS_CALENDAR_SYNC: {
    label: 'Agenda do Google (nome antigo)',
    descricao:
      'Versão antiga do mesmo ajuste. Use "Agenda do Google" — esta aqui existe só pra não quebrar configurações antigas.',
    categoria: 'Calendar',
  },
  TASKS_CALENDAR_SYNC_ENABLED: {
    label: 'Agenda do Google',
    descricao: 'Joga as tarefas do sistema pra agenda do Google de cada pessoa.',
    categoria: 'Calendar',
  },
  TASKS_MULTI_WORKFLOW_ENABLED: {
    label: 'Tarefas de todos os fluxos no card',
    descricao:
      'O card passa a mostrar as tarefas de todos os fluxos de trabalho, não só as do fluxo em que ele está.',
    categoria: 'Calendar',
  },
  AGENDA_RANGE_TOGGLE: {
    label: 'Hoje / semana / mês na agenda',
    descricao: 'Botões pra trocar o período mostrado no quadro de agenda da tela inicial.',
    categoria: 'Calendar',
  },
  AGENDA_PREVIEW_INLINE_ENABLED: {
    label: 'Próximos compromissos no card',
    descricao: 'Mostra os próximos compromissos direto dentro do card, sem precisar abrir a agenda.',
    categoria: 'Calendar',
  },
  TASK_PERMISSIONS_ENABLED: {
    label: 'Agendamento com aprovação',
    descricao: 'Quem não tem autonomia precisa da aprovação de um responsável pra agendar.',
    categoria: 'Calendar',
  },
  TASK_INVITATIONS_ENABLED: {
    label: 'Convidar gente pra uma tarefa',
    descricao: 'Permite chamar outras pessoas pra participar de uma tarefa.',
    categoria: 'Calendar',
  },
  ACTIVITY_TYPE_VISIBILITY_ENABLED: {
    label: 'Quem vê cada tipo de atividade',
    descricao:
      'Permite escolher se um tipo de atividade aparece pra todo mundo ou só em certos fluxos, departamentos ou pessoas.',
    categoria: 'Calendar',
  },
  ATIVIDADE_UNIFICADA_ENABLED: {
    label: 'Uma janela só pra nova atividade',
    descricao:
      'Junta "agendar" e "registrar o que já foi feito" numa janela só — o sistema decide se pede horário.',
    categoria: 'Calendar',
  },

  // ─── Ordens de serviço ───────────────────────────────────────────
  SERVICE_ORDERS_ENABLED: {
    label: 'Ordens de serviço',
    descricao: 'Liga o menu de Manutenção e as ordens de serviço.',
    categoria: 'OS Digital',
  },
  OS_SIGNATURE_ENABLED: {
    label: 'Assinatura do cliente',
    descricao: 'O cliente assina com o dedo na tela ao final do atendimento.',
    categoria: 'OS Digital',
  },
  AUVO_IMPORT_LEGACY_ENABLED: {
    label: 'Importação do Auvo (desativada)',
    descricao: 'Trazia dados em lote do sistema antigo Auvo. Não é mais usada.',
    categoria: 'OS Digital',
  },
  OS_PUBLIC_PDF_ENABLED: {
    label: 'PDF da ordem de serviço pro cliente',
    descricao: 'Botão de baixar a ordem de serviço em PDF na página que o cliente acessa.',
    categoria: 'OS Digital',
  },
  QUESTIONARIO_V2_ENABLED: {
    label: 'Questionários novos',
    descricao:
      'Montador de questionário com 11 tipos de pergunta, perguntas que só aparecem conforme a resposta anterior, e escolha do que o cliente pode ver.',
    categoria: 'OS Digital',
  },
  OS_PUBLIC_DIAGNOSTICO_ENABLED: {
    label: 'Diagnóstico automático pro cliente',
    descricao: 'Monta um resumo do problema a partir das respostas do questionário.',
    categoria: 'OS Digital',
  },
  OS_PUBLIC_RESPOSTAS_ENABLED: {
    label: 'Respostas e fotos pro cliente',
    descricao: 'Mostra ao cliente as respostas marcadas como públicas e as fotos do atendimento.',
    categoria: 'OS Digital',
  },

  // ─── Automações ──────────────────────────────────────────────────
  AUTOMATIONS_ACTIVITY_TRIGGERS_ENABLED: {
    label: 'Automações a partir de atividades',
    descricao:
      'Dispara automações quando alguém cria uma atividade, cria uma tarefa ou conclui uma tarefa.',
    categoria: 'Automations',
  },
  CELEBRATIONS_ENABLED: {
    label: 'Comemoração ao ganhar',
    descricao: 'Confete na tela quando um negócio é ganho ou uma etapa importante é concluída.',
    categoria: 'Automations',
  },

  // ─── Interface ───────────────────────────────────────────────────
  FEATURE_FLAGS_ADMIN_UI_ENABLED: {
    label: 'Esta tela de recursos em teste',
    descricao: 'Liga esta própria tela. Administradores sempre a enxergam.',
    categoria: 'UX',
  },
};

const FALLBACK_METADATA = (name: string): FlagMetadata => ({
  label: name,
  descricao: 'Recurso novo, ainda sem explicação cadastrada. Na dúvida, deixe como está.',
  categoria: 'Outro',
});

function getMetadata(name: string): FlagMetadata {
  return FLAG_METADATA[name] ?? FALLBACK_METADATA(name);
}

function formatTimestamp(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** De onde vem o valor atual — em português, não DB/ENV/DEFAULT. */
const SOURCE_LABEL: Record<FeatureFlagSource, string> = {
  db: 'Alterado aqui',
  env: 'Definido no servidor',
  default: 'Padrão',
};

const SOURCE_VARIANT: Record<
  FeatureFlagSource,
  'success' | 'warning' | 'info' | 'neutral' | 'danger'
> = {
  db: 'info',
  env: 'warning',
  default: 'neutral',
};

export function FeatureFlagsPage() {
  const { data: flags = [], isLoading, error } = useFeatureFlags();
  const { update, reset, pending } = useFeatureFlagActions();
  const confirm = useConfirmDialog();
  const { show: showToast } = useToast();

  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState<FlagCategoria | ''>('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return flags.filter((f) => {
      const meta = getMetadata(f.name);
      if (categoria && meta.categoria !== categoria) return false;
      if (q) {
        const hay = `${f.name} ${meta.label} ${meta.descricao}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [flags, search, categoria]);

  const ligados = useMemo(() => flags.filter((f) => f.enabled).length, [flags]);

  async function handleToggle(flag: FeatureFlagDto, nextEnabled: boolean) {
    const meta = getMetadata(flag.name);
    // M-0 SECURITY hardening — recursos críticos exigem confirmação destructive
    // antes de ATIVAR. Desativar (nextEnabled=false) sempre passa direto pra
    // evitar fricção em emergências.
    if (meta.critica && nextEnabled) {
      const ok = await confirm({
        title: `${meta.label} é uma opção CRÍTICA`,
        message:
          meta.descricao +
          '\n\nEsta opção nunca deve ficar ligada com o sistema no ar. Tem certeza absoluta?',
        confirmLabel: 'Sim, ligar mesmo assim',
        cancelLabel: 'Cancelar',
        variant: 'destructive',
      });
      if (!ok) return;
    }

    try {
      await update(flag.name, { enabled: nextEnabled });
      showToast(
        `${meta.label} ${nextEnabled ? 'ligado' : 'desligado'}`,
        { variant: 'success' },
      );
    } catch (err) {
      const detalhe = err instanceof Error ? err.message : null;
      showToast(
        detalhe
          ? `Não foi possível alterar "${meta.label}". Detalhe: ${detalhe}`
          : `Não foi possível alterar "${meta.label}". Tente de novo.`,
        { variant: 'error' },
      );
    }
  }

  async function handleReset(flag: FeatureFlagDto) {
    const meta = getMetadata(flag.name);
    const ok = await confirm({
      title: 'Voltar ao padrão?',
      message: `"${meta.label}" volta a valer o que está definido no servidor (ou o padrão de fábrica). A alteração feita por aqui é descartada.`,
      confirmLabel: 'Voltar ao padrão',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await reset(flag.name);
      showToast(`${meta.label} voltou ao padrão`, { variant: 'info' });
    } catch (err) {
      const detalhe = err instanceof Error ? err.message : null;
      showToast(
        detalhe
          ? `Não foi possível voltar ao padrão. Detalhe: ${detalhe}`
          : 'Não foi possível voltar ao padrão. Tente de novo.',
        { variant: 'error' },
      );
    }
  }

  return (
    <div className="min-w-0 space-y-6" data-testid="feature-flags-page">
      <PageHeader
        crumb="Configurações · Sistema"
        title="Recursos em teste"
        subtitle="Funcionalidades que podem ser ligadas e desligadas na hora, sem atualizar o sistema — para todo mundo ou só para quem você liberar."
      />

      <Alert variant="info" rail>
        A mudança vale na hora, mas outras telas podem levar até 5 segundos pra perceber. Os
        recursos marcados como críticos pedem uma confirmação antes de serem ligados.
      </Alert>

      {/* Busca + filtro */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="min-w-0 flex-1">
          <label
            htmlFor="ff-search"
            className="mb-1 block text-xs font-medium text-ink-2 dark:text-ink-dark-2"
          >
            Buscar
          </label>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3 dark:text-ink-dark-3" />
            <Input
              id="ff-search"
              data-testid="ff-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome do recurso ou parte da descrição…"
              className="pl-9"
            />
          </div>
        </div>
        <div className="min-w-0 md:w-64">
          <label
            htmlFor="ff-categoria"
            className="mb-1 block text-xs font-medium text-ink-2 dark:text-ink-dark-2"
          >
            Área
          </label>
          <Select
            id="ff-categoria"
            data-testid="ff-categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value as FlagCategoria | '')}
          >
            <option value="">Todas</option>
            {CATEGORIAS.map((c) => (
              <option key={c} value={c}>
                {CATEGORIA_LABEL[c]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Carregando */}
      {isLoading && (
        <Card data-testid="ff-loading">
          <p className="p-4 text-sm italic text-ink-3 dark:text-ink-dark-3">
            Carregando recursos…
          </p>
        </Card>
      )}

      {/* Erro */}
      {error && !isLoading && (
        <Alert variant="danger" rail data-testid="ff-error">
          Não foi possível carregar a lista de recursos. Tente recarregar a página.
          {error instanceof Error && (
            <span className="mt-1 block text-xs text-ink-2 dark:text-ink-dark-2">
              Detalhe: {error.message}
            </span>
          )}
        </Alert>
      )}

      {/* Vazio (sem recursos OU filtro sem resultado) */}
      {!isLoading && !error && filtered.length === 0 && (
        <div data-testid="ff-empty">
          <EmptyState
            title="Nenhum recurso encontrado"
            description={
              search || categoria
                ? 'Nenhum recurso bate com os filtros. Limpe ou ajuste a busca.'
                : 'Ainda não há recursos cadastrados.'
            }
          />
        </div>
      )}

      {/* Tabela */}
      {!isLoading && filtered.length > 0 && (
        <Card data-testid="ff-table-card">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
            <p className="min-w-0 text-sm text-ink-2 dark:text-ink-dark-2">
              {filtered.length} {filtered.length === 1 ? 'recurso' : 'recursos'} na lista ·{' '}
              {ligados} {ligados === 1 ? 'ligado' : 'ligados'} no total
            </p>
          </div>
          <Table data-testid="ff-table">
            <TableHeader>
              <TableRow>
                <TableHead>Recurso</TableHead>
                <TableHead>Ligado</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead>Última alteração</TableHead>
                <TableHead align="right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((flag) => {
                const meta = getMetadata(flag.name);
                const isPending = pending === flag.name;
                return (
                  <TableRow
                    key={flag.name}
                    data-testid={`ff-row-${flag.name}`}
                    className="align-top"
                  >
                    <TableCell className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink-1 dark:text-ink-dark-1">
                          {meta.label}
                        </span>
                        {meta.critica && (
                          <Badge variant="danger" size="sm">
                            CRÍTICA
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 max-w-[52ch] text-xs text-ink-2 dark:text-ink-dark-2">
                        {meta.descricao}
                      </p>
                      <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                        {CATEGORIA_LABEL[meta.categoria]} ·{' '}
                        <code className="break-words font-mono">{flag.name}</code>
                      </p>
                    </TableCell>
                    <TableCell>
                      <Switch
                        id={`ff-toggle-${flag.name}`}
                        data-testid={`ff-toggle-${flag.name}`}
                        checked={flag.enabled}
                        disabled={isPending}
                        aria-label={`${meta.label} ligado`}
                        onChange={(e) => handleToggle(flag, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant={SOURCE_VARIANT[flag.source]} size="sm">
                        {SOURCE_LABEL[flag.source]}
                      </Badge>
                      {flag.envValue !== null && flag.source === 'env' && (
                        <p
                          className="mt-1 break-words font-mono text-xs text-ink-3 dark:text-ink-dark-3"
                          title={`${flag.name}=${flag.envValue}`}
                        >
                          {flag.envValue}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-ink-2 dark:text-ink-dark-2">
                      {flag.override ? (
                        <>
                          <p className="whitespace-nowrap">
                            {formatTimestamp(flag.override.changedAtIso)}
                          </p>
                          <p className="text-ink-3 dark:text-ink-dark-3">
                            por {flag.override.changedByName ?? '—'}
                          </p>
                          {flag.override.notes && (
                            <p
                              className="mt-0.5 line-clamp-2 text-xs italic"
                              title={flag.override.notes}
                            >
                              &ldquo;{flag.override.notes}&rdquo;
                            </p>
                          )}
                        </>
                      ) : (
                        <span className="italic text-ink-3 dark:text-ink-dark-3">
                          nunca alterado
                        </span>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {flag.source === 'db' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="whitespace-nowrap"
                          data-testid={`ff-reset-${flag.name}`}
                          disabled={isPending}
                          onClick={() => handleReset(flag)}
                        >
                          Voltar ao padrão
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
